import { LABEL_KEY_ALERTS_DISABLED } from 'services/SensorHelper';

/* @ngInject */
export default class DisableAlertsModalController {
    constructor(DialogService, ToastService, SensorService, AlertsAndRulesService, $state, $scope, device, alert, onSuccess) {
        this.DialogService = DialogService;
        this.ToastService = ToastService;
        this.SensorService = SensorService;
        this.AlertsAndRulesService = AlertsAndRulesService;
        this.$state = $state;
        this.$scope = $scope;
        this.device = device;
        this.alert = alert;
        this.onSuccess = onSuccess;
    }

    $onInit() {
        this.pausing = false;
    }

    get targetDevice() {
        // Use device from alert if available, otherwise use direct device
        return this.alert?.device || this.device;
    }

    get deviceTypeName() {
        return this.targetDevice?.type === 'ccon' ? 'Cloud Connector' : 'sensor';
    }

    get isFromAlert() {
        return !!this.alert;
    }

    pauseAlerts() {
        this.pausing = true;

        // Check if label already exists
        if (this.targetDevice.labels && Object.prototype.hasOwnProperty.call(this.targetDevice.labels, LABEL_KEY_ALERTS_DISABLED)) {
            this.ToastService.showSimpleText(`Alerts already paused for ${this.deviceTypeName}`);
            this.pausing = false;
            return;
        }

        const deviceResourceName = this.targetDevice.name;
        const deviceDisplayName = this.targetDevice.labels?.name || this.targetDevice.id;

        // Create the label object with key and empty value
        const alertsDisabledLabel = { key: LABEL_KEY_ALERTS_DISABLED, value: '' };

        // First archive all active alerts for this device with a comment
        // This must be done before adding the label, as the backend may automatically
        // archive alerts when the device gets the pause label
        this.archiveActiveAlerts(deviceResourceName)
            .then(() => {
                // Then add the LABEL_KEY_ALERTS_DISABLED label to the device
                return this.SensorService.createLabel(deviceResourceName, alertsDisabledLabel);
            })
            .then(() => {
                this.ToastService.showSimpleText(`Alerts paused for ${deviceDisplayName}`);
                
                if (this.isFromAlert) {
                    // Reload the state to refresh the alert list
                    this.$state.reload();
                } else {
                    // Update the device object and notify parent
                    this.updateDeviceAndNotifyParent();
                }
                
                this.closeModal();
            })
            .catch((error) => {
                console.error('Failed to pause alerts:', error); // eslint-disable-line no-console
                this.ToastService.showSimpleText('Failed to pause alerts');
                this.pausing = false;
            });
    }

    archiveActiveAlerts(deviceResourceName) {
        // Get all active alerts for this device
        return this.AlertsAndRulesService.listActiveAlertsForDevice(deviceResourceName)
            .then(response => {
                const alerts = response.data || [];
                
                if (alerts.length === 0) {
                    // No active alerts to archive
                    return Promise.resolve();
                }

                // Archive each active alert for the device with a comment
                const archivePromises = alerts.map(alert => {
                    const comment = 'Paused alerts for device.';
                    return this.AlertsAndRulesService.archiveAlert(alert, comment)
                        .catch(error => {
                            // Log error but don't fail the entire operation
                            console.error(`Failed to archive alert ${alert.name}:`, error); // eslint-disable-line no-console
                            return null; // Return null to indicate this archive failed
                        });
                });

                return Promise.all(archivePromises);
            })
            .catch(error => {
                // Log error but don't fail the entire pause operation
                console.error('Failed to get active alerts for device:', error); // eslint-disable-line no-console
                return Promise.resolve(); // Continue with pause operation even if archiving fails
            });
    }

    updateDeviceAndNotifyParent() {
        // Update the device's labels to include the alerts disabled label
        if (!this.targetDevice.labels) {
            this.targetDevice.labels = {};
        }
        this.targetDevice.labels[LABEL_KEY_ALERTS_DISABLED] = '';

        // Trigger UI update
        this.$scope.$applyAsync();

        // Notify parent component if callback is provided
        if (this.onSuccess) {
            this.onSuccess();
        }
    }

    closeModal() {
        this.DialogService.cancel();
    }
}
